import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Check, Ban, Clock } from 'lucide-react';
import api from '@/lib/api';
import { useAttachmentImages } from '@/hooks/useAttachmentImages';
import { AttachmentImage } from '@/components/AttachmentImage';
import { FileRequestTrace } from '@/components/FileRequestTrace';
import { useFileRequestCategories, labelFor, useFileRequestCopy, copyFor }
  from '@/lib/fileRequestCategories';

/**
 * Admin/staff view of an open file request, with the two close actions.
 *
 * WHY THIS EXISTS: until now the drawer showed admin NOTHING about file requests —
 * FileRequestPanel is gated `user.role === 'editor'` — while both escalation paths
 * ("cannot_provide" and the 48h staleness sweep) claimed to reach admin. They
 * reached a notification, which routed to a drawer with no controls, and the two
 * endpoints built for admin (`needs-attention`, `close`) had zero callers. So the
 * single most common outcome of the feature dead-ended on the one person who was
 * supposed to unblock it.
 *
 * FULL EXCHANGE, NOT A SUMMARY. Admin sees exactly what the editor sees plus what
 * the client said: the category, the editor's note, the screenshots, and the
 * client's verdict and prose. This is the one role permitted to see both sides, and
 * being asked to intervene on a redacted version of an argument is how people make
 * confident wrong calls. (Note the direction of the asymmetry: the CLIENT never
 * sees the editor's identity — server.redact_for_client strips it — but admin sees
 * everything, so `created_by_name` is safe to render here and only here.)
 *
 * The brief card on the dashboard is a POINTER to this panel, not a second place to
 * act. State changes live here, where the exchange is on screen.
 */

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US',
      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

/** When the request went out. A time for today, a date for anything older —
 *  never an elapsed-hours count, which produced "0h" for anything under an hour. */
const sentLabel = (iso) => {
  try {
    const d = new Date(iso);
    const isToday = d.toDateString() === new Date().toDateString();
    return {
      isToday,
      time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
  } catch { return { isToday: false, time: '', date: '' }; }
};

const hoursSince = (iso) => {
  if (!iso) return 0;
  try { return Math.floor((Date.now() - new Date(iso).getTime()) / 3600000); }
  catch { return 0; }
};

export function AdminFileRequest({ project, onRefresh }) {
  const req = (project.file_requests || []).find(r => r.status === 'open');
  const [saving, setSaving] = useState(false);
  const { imageState, retryImage, reportLoaded, reportError } =
    useAttachmentImages(req?.images, { transport: 'blob' });
  const categories = useFileRequestCategories();
  const copy = useFileRequestCopy();
  const t = (key) => copyFor(copy, key, req?.category || '');

  // No open request: show the most recent CLOSED one instead of nothing — the
  // admin arriving from a "client responded" email needs the exchange they were
  // told about, not an empty tab (Angelle + John, 2026-08-06).
  if (!req) return <FileRequestTrace project={project} categories={categories} copy={copy} />;

  const answered = (req.responses || []).slice(-1)[0];
  const cannotProvide = answered?.verdict === 'cannot_provide';
  const stale = !answered && hoursSince(req.created_at) >= 48;

  const close = async (action) => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/file-requests/${req.request_number}/close`,
                    { action });
      toast.success(action === 'resolve'
        ? 'Marked resolved — the editor has been told'
        : 'Cancelled — the editor has been told');
      if (onRefresh) onRefresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' && (d?.message || d?.error)
        ? (d.message || d.error) : 'Could not close the request');
      if (onRefresh) onRefresh();
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl bg-ug-raised border border-ug-gold/40 overflow-hidden"
         data-testid="admin-file-request">
      {/* Slab carries the REASON, not a generic title. The two triggers mean
          different things and want different responses from a human: one is a
          client who has told us they cannot help, the other is silence. */}
      <div className="flex items-center gap-2 bg-ug-gold px-4 py-2.5"
           data-testid="admin-file-request-slab">
        {cannotProvide
          ? <AlertTriangle className="w-4 h-4 shrink-0 text-ug-deepest" />
          : <Clock className="w-4 h-4 shrink-0 text-ug-deepest" />}
        <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-deepest">
          {cannotProvide ? t('admin_panel.slab_cannot_provide')
            : stale ? t('admin_panel.slab_stale')
            : t('admin_panel.slab_open')}
        </span>
      </div>

      <div className="p-5">
        <p className="font-sans text-[10px] text-zinc-500">
          Request #{req.request_number} &middot; raised by{' '}
          <span className="text-zinc-300">{req.created_by_name || 'the editor'}</span>{' '}
          {fmtWhen(req.created_at)}
        </p>

        {/* What the editor asked — identical content to the editor's own panel. */}
        <div className="mt-3 rounded-md bg-ug-surface border border-ug-border px-3 py-2.5"
             data-testid="admin-file-request-ask">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block">
            {labelFor(categories, req.category, 'Something')}
          </span>
          <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{req.note}</p>
          {(req.images || []).length > 0 && (
            <div className="mt-2.5 space-y-2" data-testid="admin-file-request-images">
              {/* Never conditional on success — see the client panel. Admin needs to
                  know a screenshot exists even when it will not load, because they
                  are the one who can go and find out why. */}
              {(req.images || []).map((img, i) => (
                <AttachmentImage key={img.file_id}
                                 state={imageState[img.file_id]}
                                 filename={img.filename}
                                 onRetry={() => retryImage(img.file_id)}
                                onLoaded={() => reportLoaded(img.file_id)}
                                onLoadError={() => reportError(img.file_id)}
                                 testId={`admin-file-request-image-${i}`} />
              ))}
            </div>
          )}
        </div>

        {/* What the client said back, or that they haven't. */}
        {answered ? (
          <div className="mt-3 rounded-md border border-ug-border px-3 py-2.5"
               data-testid="admin-file-request-client-reply">
            {/* NO files_uploaded BRANCH — same reason as the editor panel: that
                verdict resolves the request, and this panel only ever holds an
                open one, so the line was unreachable. It had already drifted from
                both the editor panel's spelling and the live email's. KNOWN-ISSUES #18. */}
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider block text-ug-status-red"
                  data-testid="admin-file-request-reply-title">
              {t('admin_panel.reply_cannot_provide')}
              <span className="text-zinc-500 font-normal normal-case tracking-normal">
                {' '}&middot; {fmtWhen(answered.responded_at)}
              </span>
            </span>
            {answered.note && (
              <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{answered.note}</p>
            )}
          </div>
        ) : (
          <p className="font-sans text-[11px] text-zinc-500 mt-3"
             data-testid="admin-file-request-no-reply">
            {(() => {
              const sent = sentLabel(req.created_at);
              return sent.isToday
                ? t('admin_panel.no_reply_today').replace('{time}', sent.time)
                : t('admin_panel.no_reply_older').replace('{date}', sent.date);
            })()}
          </p>
        )}

        {/* THE TWO ACTIONS MEAN DIFFERENT THINGS AND WRITE DIFFERENT HISTORY.
            They are not two ways to make the panel go away:

              Resolve -> resolution "admin_resolve"  -> the need was MET (the files
                         turned up another way, a call sorted it). The request did
                         its job.
              Cancel  -> resolution "admin_cancel"   -> the request was WRONG to
                         send (misrouted, a broken link on our end, asked in error).

            So each button states its meaning above itself rather than relying on the
            verb, and cancel is the only destructive-coloured control on the panel.
            An admin who picks the wrong one writes the wrong sentence into an audit
            trail nobody re-reads until it matters. */}
        {/* `items-end` rather than a fixed height on the captions. The captions are
            different lengths and wrap at different widths, so reserving two lines for
            both left a visible gap under whichever one fitted on one line. Aligning
            the grid items at their bottoms keeps the buttons on the same line however
            the text wraps, without reserving space nothing occupies. */}
        <div className="mt-4 grid grid-cols-2 gap-2 items-end" data-testid="admin-file-request-actions">
          <div>
            <p className="font-sans text-[9px] text-zinc-500 leading-snug mb-1.5">
              Sorted &mdash; the editor has what they need.
            </p>
            <button onClick={() => close('resolve')} disabled={saving}
                    data-testid="admin-file-request-resolve"
                    className="tap-auto w-full flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[11px] font-semibold text-zinc-200 hover:border-oxidized-teal/50 transition-colors disabled:opacity-50">
              <Check className="w-3.5 h-3.5" /> Resolve
            </button>
          </div>
          <div>
            <p className="font-sans text-[9px] text-zinc-500 leading-snug mb-1.5">
              Shouldn&rsquo;t have gone out &mdash; withdraw it.
            </p>
            <button onClick={() => close('cancel')} disabled={saving}
                    data-testid="admin-file-request-cancel"
                    className="tap-auto w-full flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-status-red/30 px-3 py-2 font-sans text-[11px] font-semibold text-ug-status-red hover:border-ug-status-red/60 transition-colors disabled:opacity-50">
              <Ban className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminFileRequest;
